import React, { useEffect, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  FlatList,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Dimensions,
} from 'react-native';
import { useMaintenanceStore } from '../../stores/maintenanceStore';
import { MaintenanceCard } from '../../components/maintenance/MaintenanceCard';
import { WorkshopTimeGraph } from '../../components/maintenance/WorkshopTimeGraph';
import { JobCardsTable } from '../../components/maintenance/JobCardsTable';
import { Button } from '../../components/ui/Button';
import { BackButton } from '../../components/ui/BackButton';
import theme from '../../theme';
import { JobCard } from '../../types';
import { Ionicons } from '@expo/vector-icons';

export const MaintenanceScreen = ({ navigation }: any) => {
  const { jobCards, loading, error, fetchJobCards } = useMaintenanceStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [activeTab, setActiveTab] = useState<'graph' | 'list'>('graph');

  // Set to 1 column for list view
  const numColumns = 1;

  useEffect(() => {
    fetchJobCards();
  }, []);

  const handleRefresh = () => {
    fetchJobCards();
  };

  const handleJobCardPress = (jobCard: JobCard) => {
    navigation.navigate('MaintenanceDetails', { jobCardId: jobCard.id });
  };

  const handleEditJobCard = (jobCard: JobCard) => {
    navigation.navigate('AddJobCard', { editingCard: jobCard });
  };

  const handleAddJobCard = () => {
    navigation.navigate('AddJobCard');
  };

  const filteredJobCards = jobCards.filter((jobCard) => {
    const matchesSearch =
      (jobCard.jobNumber?.toString() || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (jobCard.licensePlate?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (jobCard.makeModel?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter ? jobCard.jobStatus === statusFilter : true;
    
    return matchesSearch && matchesStatus;
  });

  const renderFilterButton = (status: string | null, label: string) => (
    <TouchableOpacity
      style={[
        styles.filterButton,
        statusFilter === status && styles.filterButtonActive,
      ]}
      onPress={() => setStatusFilter(status)}
    >
      <Text
        style={[
          styles.filterButtonText,
          statusFilter === status && styles.filterButtonTextActive,
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );

  if (error) {
    return (
      <View style={styles.centered}>
        <Ionicons name="warning-outline" size={48} color={theme.colors.error} />
        <Text style={styles.errorText}>{error}</Text>
        <Button title="Try Again" onPress={handleRefresh} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <BackButton 
        onPress={() => navigation.goBack()}
        style={styles.backButtonPosition}
      />
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text style={styles.title}>Maintenance</Text>
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={[styles.addButton, { marginRight: 10 }]}
              onPress={() => navigation.navigate('WorkshopCreditReport')}
            >
              <Ionicons name="document-text" size={24} color={theme.colors.white} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.addButton}
              onPress={handleAddJobCard}
            >
              <Ionicons name="add" size={24} color={theme.colors.white} />
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Search */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <Ionicons name="search" size={20} color={theme.colors.textSecondary} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search by job #, plate, or vehicle..."
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>
      </View>

      {/* Filters */}
      <View style={styles.filtersContainer}>
        <Text style={styles.filtersTitle}>Status:</Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filtersScrollContent}
        >
          {renderFilterButton(null, 'All')}
          {renderFilterButton('pending', 'Pending')}
          {renderFilterButton('in_progress', 'In Progress')}
          {renderFilterButton('completed', 'Completed')}
          {renderFilterButton('handed_over', 'Handed Over')}
        </ScrollView>
      </View>

      {/* Stats Row */}
      <View style={styles.statsContainer}>
        <View style={styles.statCard}>
          <Text style={styles.statNumber}>{jobCards.length}</Text>
          <Text style={styles.statLabel}>Total Jobs</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statNumber}>
            {jobCards.filter(job => job.jobStatus === 'in_progress').length}
          </Text>
          <Text style={styles.statLabel}>In Progress</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statNumber}>
            {jobCards.filter(job => job.jobStatus === 'pending').length}
          </Text>
          <Text style={styles.statLabel}>Pending</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statNumber}>
            {jobCards.filter(job => job.jobStatus === 'completed').length}
          </Text>
          <Text style={styles.statLabel}>Completed</Text>
        </View>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'graph' && styles.tabActive]}
          onPress={() => setActiveTab('graph')}
        >
          <Ionicons 
            name="bar-chart-outline" 
            size={20} 
            color={activeTab === 'graph' ? theme.colors.primary : theme.colors.textSecondary} 
          />
          <Text style={[styles.tabText, activeTab === 'graph' && styles.tabTextActive]}>
            Graph
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'list' && styles.tabActive]}
          onPress={() => setActiveTab('list')}
        >
          <Ionicons 
            name="list-outline" 
            size={20} 
            color={activeTab === 'list' ? theme.colors.primary : theme.colors.textSecondary} 
          />
          <Text style={[styles.tabText, activeTab === 'list' && styles.tabTextActive]}>
            Job Cards
          </Text>
        </TouchableOpacity>
      </View>

      {/* Tab Content */}
      {activeTab === 'graph' ? (
        <WorkshopTimeGraph jobCards={jobCards.filter(jc => jc.jobNumber?.startsWith('WS-') || false)} />
      ) : (
        loading && jobCards.length === 0 ? (
          <View style={styles.centered}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={styles.loadingText}>Loading maintenance jobs...</Text>
          </View>
        ) : filteredJobCards.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="construct-outline" size={64} color={theme.colors.gray300} />
            <Text style={styles.emptyText}>No maintenance jobs found</Text>
            <Text style={styles.emptySubtext}>Create your first job card to get started</Text>
            <Button
              title="Create Job Card"
              onPress={handleAddJobCard}
              variant="primary"
              style={styles.emptyButton}
            />
          </View>
        ) : (
          <JobCardsTable
            jobCards={filteredJobCards}
            onRowPress={handleJobCardPress}
            onEdit={handleEditJobCard}
          />
        )
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  backButtonPosition: {
    position: 'absolute',
    top: 20,
    left: 20,
    zIndex: 10,
  },
  header: {
    backgroundColor: theme.colors.white,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    ...theme.shadows.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  addButton: {
    backgroundColor: theme.colors.primary,
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.shadows.sm,
  },
  searchContainer: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  searchInput: {
    flex: 1,
    marginLeft: theme.spacing.sm,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textPrimary,
  },
  filtersContainer: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  filtersTitle: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    marginBottom: theme.spacing.sm,
    color: theme.colors.textSecondary,
  },
  filtersScrollContent: {
    paddingBottom: theme.spacing.xs,
  },
  filterButton: {
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.round,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    marginRight: theme.spacing.sm,
  },
  filterButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  filterButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  filterButtonTextActive: {
    color: theme.colors.white,
  },
  statsContainer: {
    flexDirection: 'row',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as const,
    color: theme.colors.primary,
  },
  statLabel: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    marginTop: 2,
  },
  gridContent: {
    padding: theme.spacing.sm,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    color: theme.colors.textSecondary,
    fontSize: theme.typography.sizes.md,
  },
  errorText: {
    color: theme.colors.error,
    fontSize: theme.typography.sizes.md,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
    marginTop: theme.spacing.md,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
  },
  emptyText: {
    fontSize: theme.typography.sizes.lg,
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.xs,
    textAlign: 'center',
    fontWeight: '600' as const,
  },
  emptySubtext: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.lg,
    textAlign: 'center',
  },
  emptyButton: {
    marginTop: theme.spacing.md,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    marginHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.sm,
    borderRadius: theme.borderRadius.lg,
    padding: 4,
    ...theme.shadows.sm,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  tabActive: {
    backgroundColor: theme.colors.primaryLight,
  },
  tabText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    fontWeight: '600' as const,
  },
  tabTextActive: {
    color: theme.colors.primary,
  },
});
